import React from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../utils/FormateDateTime";
import { CalculateAge } from "../../utils/CalculateAge";
import moment from "moment";
import ChaiReportHeader from "../../shared/ChaiReportHeader";
import ChaiReportFooter from "../../shared/ChaiReportFooter";
import useMedicalReportSettings from "../../hooks/useMedicalReportSettings";
import ReportBarcode from './ReportBarcode';

const RadiologyReportPrint = ({ orderData: rawOrderData, selectedItemId }) => {
  const { t, i18n } = useTranslation();
  const { isChaiFormat, businessInfo: buisnessInfo } = useMedicalReportSettings();
  const orderData = rawOrderData?.data?.result || rawOrderData?.result || rawOrderData?.data || rawOrderData;
  const reportSettings = rawOrderData?.data?.settings || rawOrderData?.settings || orderData?.settings || [];
  const getReportSetting = (key, fallback) => {
    const setting = Array.isArray(reportSettings)
      ? reportSettings.find(item => item?.key === key)
      : reportSettings?.[key];
    return setting?.value ?? setting ?? fallback;
  };
  const isReportSettingEnabled = (key, fallback) => {
    const value = getReportSetting(key, fallback);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return !['false', '0', 'no', 'off'].includes(value.toLowerCase());
    return Boolean(value);
  };
  const showCbahiLogo = isReportSettingEnabled('is_cbahi_logo', isChaiFormat);
  const showStandardLogo = isReportSettingEnabled('is_standard_logo', true);
  const showBarcode = isReportSettingEnabled('show_barcode', isChaiFormat);
  const reportPrintHeader = String(getReportSetting('report_print_header', '')).toLowerCase();
  const printReportLayout = getReportSetting('print_report_layout', 'compact_report');
  const isComprehensiveReport = printReportLayout === 'comprehensive_report';
  const useCbahiHeader = reportPrintHeader
    ? reportPrintHeader.includes('cbahi')
    : showCbahiLogo || isChaiFormat;

  const getBusinessLogo = () => {
    if (!buisnessInfo) return "";
    let logo = buisnessInfo?.logo || buisnessInfo?.logo_url || "";
    if (logo && !logo.startsWith("data:") && !logo.startsWith("http")) {
      logo = `data:image/jpeg;base64,${logo}`;
    }
    return logo;
  };
  const businessLogo = getBusinessLogo();

  const getPerformerInfo = () => {
    const performerInfo = {
      receivedBy: '---',
      approvedBy: '---',
      receivedDate: '---',
      approvedDate: '---',
      signatureUrl: '',
      stampUrl: ''
    };

    const items = orderData?.items || orderData?.order_items || [];

    const allHistory = [];
    items.forEach(item => {
      const history = Array.isArray(item?.history) ? item.history : [];
      if (history.length > 0) {
        allHistory.push(...history);
        return;
      }

      [
        [item?.received_info, 'Order Item Received'],
        [item?.result_upload_info, 'Order Item Result Uploaded'],
        [item?.approved_info, 'Order Item Approved']
      ].forEach(([info, reason]) => {
        if (info?.at) {
          allHistory.push({ action_info: info, action_reason: reason });
        }
      });
    });

    allHistory.forEach(h => {
      const reason = h.action_reason?.toLowerCase() || '';
      const performer = h.action_performers?.name || h.action_info?.by_name || '---';
      const time = h.action_performers?.time || h.action_info?.at;

      if (reason.includes('order item received') || reason.includes('received')) {
        performerInfo.receivedBy = performer;
        if (time) {
          performerInfo.receivedDate = moment.parseZone(time).format('DD MMM YYYY hh:mm A');
        }
      } else if (reason.includes('order item result uploaded') || reason.includes('result uploaded') || reason.includes('approved')) {
        performerInfo.approvedBy = performer;
        performerInfo.signatureUrl = h.action_info?.signature_url || h.action_performers?.signature_url || performerInfo.signatureUrl;
        performerInfo.stampUrl = h.action_info?.stamp_url || h.action_performers?.stamp_url || performerInfo.stampUrl;
        if (time) {
          performerInfo.approvedDate = moment.parseZone(time).format('DD MMM YYYY hh:mm A');
        }
      }
    });

    return performerInfo;
  };

  const performerInfo = getPerformerInfo();

  const getItemsToPrint = () => {
    if (orderData?.components && orderData.components.length > 0) {
      return [orderData];
    }

    const items = orderData?.items || orderData?.order_items || [];

    if (selectedItemId && selectedItemId !== "all") {
      return items.filter((item) =>
        item.id === selectedItemId || item._id === selectedItemId || item.item_id === selectedItemId
      );
    }

    const itemsWithResults = items.filter(item => item?.result_upload);

    if (itemsWithResults.length > 0) {
      return itemsWithResults;
    }

    return items.filter(
      (item) =>
        (item?.progess_status || item?.progressstatus || "").toLowerCase() === "completed"
    );
  };

  const itemsToPrint = getItemsToPrint();
  const getItemCategory = (item) => item?.test_category || item?.category || item?.result_upload?.test_category || item?.result_upload?.category || 'Other';
  const orderedItemsToPrint = isComprehensiveReport
    ? [...itemsToPrint].sort((a, b) => String(getItemCategory(a)).localeCompare(String(getItemCategory(b))))
    : itemsToPrint;

  if (!itemsToPrint || itemsToPrint.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">{t("No report data available")}</p>
      </div>
    );
  }

  const patient = orderData?.patient || orderData?.patient_info;
  const practitioner = orderData?.practitioner || orderData?.practitioner_info;
  const orderNo = orderData?.order_no || orderData?.order_number || '---';
  const reportBarcode = orderData?.barcode_data || itemsToPrint[0]?.barcode_data || itemsToPrint[0]?.result_upload?.barcode_data || orderNo;
  const showReportBarcode = showBarcode && Boolean(reportBarcode && reportBarcode !== '---');
  const orderDates = {
    received: performerInfo.receivedDate,
    uploaded: performerInfo.approvedDate
  };
  const repeatedPrintHeader = (
    <div className='print-report-header bg-white'>
      {useCbahiHeader ? (
        <ChaiReportHeader reportName={t('Radiology Report')} businessInfo={buisnessInfo} showChaiLogo={showCbahiLogo} showBusinessLogo={showStandardLogo} settingsOverride />
      ) : (
        <div className='flex justify-between items-end mb-2 px-3 pt-3 pb-2'>
          <div className='w-[30%] text-[11px] text-gray-600'>
            <div><strong>License No:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo || 'N/A'}</div>
            <div><strong>CR No:</strong> {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}</div>
            <div><strong>Phone:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}</div>
            <div><strong>Email:</strong> {buisnessInfo?.email || 'N/A'}</div>
          </div>
          <div className='w-[40%] flex flex-col items-center'>
            {showStandardLogo && businessLogo && <img className='h-[65px] mb-2 object-contain' src={businessLogo} alt='Business Logo' />}
            <p className='text-red-700 font-bold text-[14px] text-center mb-1 leading-tight'>{buisnessInfo?.name_en || 'Business Name'}<br />{buisnessInfo?.name_ar || 'Business Name'}</p>
            <p className='text-gray-600 text-[12px] font-semibold text-center border-t border-gray-100 pt-1'>{t('Radiology Report')}</p>
          </div>
          <div className='w-[30%] text-[11px] text-gray-600 text-right' dir='rtl'>
            <div><strong>رقم ترخيص:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo || 'N/A'}</div>
            <div><strong>رقم السجل التجاري:</strong> {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}</div>
            <div><strong>الهاتف:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}</div>
            <div><strong>البريد الإلكتروني:</strong> {buisnessInfo?.email || 'N/A'}</div>
          </div>
        </div>
      )}
      <div className='mx-3 mb-2 border border-black p-2 text-[12px] rounded-sm'>
        <div className='grid grid-cols-2 gap-x-8 gap-y-[1px]'>
          <div className='col-span-2 flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Patient Name')}:</strong><span className='capitalize font-semibold text-gray-900'>{patient?.name || patient?.full_name || orderData?.subject?.name?.Given || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Type')}:</strong><span className='text-gray-900 font-semibold'>{t('Radiology')}</span></div>
          <div className='flex items-start gap-2'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Order Number')}:</strong><div className='flex min-w-0 flex-col items-start gap-1'><span className='whitespace-nowrap text-gray-900 font-semibold'>{orderNo}</span>{showReportBarcode && <ReportBarcode value={reportBarcode} />}</div></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('MRN')}:</strong><span className='text-gray-900 font-semibold'>{patient?.file_no || patient?.file_number || orderData?.subject?.file_number || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Doctor')}:</strong><span className='text-gray-900 font-semibold'>{practitioner?.name || orderData?.requester?.Name?.Given || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Document ID')}:</strong><span className='text-gray-900 font-semibold'>{patient?.document_id || orderData?.subject?.document_id || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Phone')}:</strong><span className='text-gray-900 font-semibold'>{patient?.phone || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Receiving Date')}:</strong><span className='text-gray-900 font-semibold'>{orderDates.received}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Age / Gender')}:</strong><span className='text-gray-900 font-semibold'>{CalculateAge(patient?.dob || orderData?.subject?.birth_date) || '---'} / {patient?.gender || orderData?.subject?.gender || '---'}</span></div>
          <div className='flex items-baseline'><strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Result Date')}:</strong><span className='text-gray-900 font-semibold'>{orderDates.uploaded}</span></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="radiology-report-container" dir={i18n.language?.startsWith('ar') ? 'rtl' : 'ltr'}>
      <div className={`report-page ${isComprehensiveReport ? 'comprehensive-report-page' : 'compact-report-page'}`}>
        <table className="print-table w-full border-collapse">
          <thead className={isComprehensiveReport ? 'comprehensive-header-repeat' : ''}>
            <tr>
              <td>
                {isComprehensiveReport ? repeatedPrintHeader : <div className="page-header-space"></div>}
              </td>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>
                <div className={`print-report-content ${isComprehensiveReport ? 'comprehensive-print-content' : 'compact-print-content'}`}>
                  <div className='print-report-header comprehensive-body-header'>
                  {/* Business Header */}
                  {useCbahiHeader ? (
                    <ChaiReportHeader
                      reportName={t("Radiology Report")}
                      businessInfo={buisnessInfo}
                      showChaiLogo={showCbahiLogo}
                      showBusinessLogo={showStandardLogo}
                      settingsOverride
                    />
                  ) : (
                    <div className='flex justify-between items-end mb-2 px-3 pt-3 pb-2'>
                      <div className='w-[30%] text-[11px] text-gray-600'>
                        <div className='mb-1'>
                          <strong>License No:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo || 'N/A'}
                        </div>
                        <div className='mb-1'>
                          <strong>CR No:</strong> {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}
                        </div>
                        <div className='mb-1'>
                          <strong>Phone:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}
                        </div>
                        <div className='mb-1'>
                          <strong>Email:</strong> {buisnessInfo?.email || 'N/A'}
                        </div>
                      </div>

                      <div className='w-[40%] flex flex-col items-center'>
                        {showStandardLogo && businessLogo ? (
                          <img
                            className='h-[65px] mb-2 object-contain'
                            src={businessLogo}
                            alt='Business Logo'
                          />
                        ) : null}
                        <p className='text-red-700 font-bold text-[14px] text-center mb-1 leading-tight'>
                          {buisnessInfo?.name_en || 'Business Name'}
                          <br />
                          {buisnessInfo?.name_ar || 'Business Name'}
                        </p>
                        <p className='text-gray-600 text-[12px] font-semibold text-center border-t border-gray-100 pt-1'>
                          Radiology Report
                        </p>
                      </div>

                      <div className='w-[30%] text-[11px] text-gray-600 text-right' dir='rtl'>
                        <div className='mb-1'>
                          <strong>رقم ترخيص:</strong> {buisnessInfo?.license_no || buisnessInfo?.licenseNo || 'N/A'}
                        </div>
                        <div className='mb-1'>
                          <strong>رقم السجل التجاري:</strong>  {buisnessInfo?.crno || buisnessInfo?.cr_number || buisnessInfo?.cr_no || 'N/A'}
                        </div>
                        <div className='mb-1'>
                          <strong>الهاتف:</strong> {buisnessInfo?.phone || buisnessInfo?.contact_info || 'N/A'}
                        </div>
                        <div className='mb-1'>
                          <strong>البريد الإلكتروني:</strong> {buisnessInfo?.email || 'N/A'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Patient Details Section */}
                  <div className='mx-3 mb-2 border border-black p-2 mt-3 text-[12px] rounded-sm'>
                    <div className='grid grid-cols-2 gap-x-8 gap-y-[1px]'>
                      <div className='col-span-2 flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Patient Name')}:</strong>
                        <span className='capitalize font-semibold text-gray-900 whitespace-normal break-words'>{patient?.name || patient?.full_name || orderData?.subject?.name?.Given || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Type')}:</strong>
                        <span className='text-gray-900 font-semibold'>{t('Radiology')}</span>
                      </div>
                      <div className='flex items-start'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Order Number')}:</strong>
                        <div className='flex min-w-0 flex-col items-start gap-1'>
                          <span className='whitespace-nowrap text-gray-900 font-semibold'>{orderNo}</span>
                          {showReportBarcode && <ReportBarcode value={reportBarcode} />}
                        </div>
                      </div>

                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('MRN')}:</strong>
                        <span className='text-gray-900 font-semibold'>{patient?.file_no || patient?.file_number || orderData?.subject?.file_number || '---'}</span>
                      </div>

                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Doctor')}:</strong>
                        <span className='capitalize text-gray-900 font-semibold whitespace-normal break-words'>{practitioner?.name || orderData?.requester?.Name?.Given || '---'}</span>
                      </div>

                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Document ID')}:</strong>
                        <span className='text-gray-900 font-semibold'>{patient?.document_id || orderData?.subject?.document_id || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Phone')}:</strong>
                        <span className='text-gray-900 font-semibold'>{patient?.phone || '---'}</span>
                      </div>

                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Gender')}:</strong>
                        <span className='capitalize text-gray-900 font-semibold'>{patient?.gender || orderData?.subject?.gender || '---'}</span>
                      </div>
                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Receiving Date')}:</strong>
                        <span className='text-gray-900 font-semibold'>{orderDates.received}</span>
                      </div>

                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Age')}:</strong>
                        <span className='text-gray-900 font-semibold'>{CalculateAge(patient?.dob || orderData?.subject?.birth_date) || '---'}</span>
                      </div>

                      <div className='flex items-baseline'>
                        <strong className='w-[130px] flex-shrink-0 text-gray-700 font-bold uppercase'>{t('Result Date')}:</strong>
                        <span className='text-gray-900 font-semibold'>{orderDates.uploaded}</span>
                      </div>
                    </div>
                  </div>

                  </div>
                  {orderedItemsToPrint.map((item, index) => {
                    const category = getItemCategory(item);
                    const previousCategory = index > 0 ? getItemCategory(orderedItemsToPrint[index - 1]) : null;
                    const isNewCategory = isComprehensiveReport && index > 0 && category !== previousCategory;
                    const itemReportData = item?.result_upload || {
                      components: item?.components || [],
                      comments: item?.comments || "",
                    };
                    const testName = item?.item_name || item?.non_standard_desc || item?.service_desc || t("Test Results");
                    const translatedTestName = t(testName);
                    const isArabic = /[\u0600-\u06FF]/.test(translatedTestName);

                    return (
                      <div
                        key={item.id || item._id || item.item_id || index}
                        className={`report-section mx-3 ${index > 0 ? 'mt-2 pt-2' : 'mt-2'}`}
                        style={isNewCategory ? { pageBreakBefore: 'always', breakBefore: 'page' } : undefined}
                      >
                        {isComprehensiveReport && (index === 0 || isNewCategory) && (
                          <div className='mx-0 mb-1 border border-gray-300 bg-gray-100 py-1 text-center text-[12px] font-bold uppercase text-gray-700'>
                            {category}
                          </div>
                        )}
                        <div className='font-bold mb-1 text-gray-900 text-left rtl:text-right text-[14px] border-gray-300 pb-1 uppercase tracking-tight flex items-center justify-start gap-1'>
                          <span dir={isArabic ? 'rtl' : 'ltr'} className="inline-flex items-center gap-1">
                            <span>{index + 1}.</span>
                            <bdi>{translatedTestName}</bdi>
                          </span>
                        </div>

                        <div className="findings-content mt-1 text-left rtl:text-right">
                          {itemReportData?.components && itemReportData.components.length > 0 ? (
                            itemReportData.components.map((component, idx) => {
                              const compName = t(component?.component_name || "---");
                              const isCompArabic = /[\u0600-\u06FF]/.test(compName);

                              return (
                                <div key={idx} className="mb-3 py-[2px] print-avoid-break">
                                  <div className="font-bold text-gray-800 text-[13px] underline mb-1">
                                    <span dir={isCompArabic ? "rtl" : "ltr"} className="inline-block">
                                      {compName}:
                                    </span>
                                  </div>
                                  <div className="text-gray-900 text-[12px] whitespace-pre-wrap leading-relaxed">
                                    {component?.component_note || "---"}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="px-3 py-2 text-center text-gray-400 italic text-[13px] uppercase tracking-widest">
                              {t("No Results Uploaded")}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Unified Comments Section at the End */}
                  {(() => {
                    const allComments = itemsToPrint
                      .map((item, i) => {
                        const testName = item?.item_name || item?.non_standard_desc || item?.service_desc || t("Test Results");
                        const itemReportData = item?.result_upload || { comments: item?.comments || "" };
                        const rawComment = itemReportData?.comments || '';
                        const comment = typeof rawComment === 'string' ? rawComment.trim() : '';

                        if (!comment ||
                          comment.toLowerCase() === "result entered by lab technician" ||
                          comment.toLowerCase() === "dont add" ||
                          comment.toLowerCase() === "n/a") {
                          return null;
                        }

                        return { i: i + 1, testName, comment };
                      })
                      .filter(Boolean);

                    if (allComments.length === 0) return null;

                    return (
                      <div className='px-3 mt-4 print-avoid-break'>
                        <div className='font-bold text-gray-900 border-b border-black pb-[2px] mb-2 flex justify-between items-center uppercase'>
                          <span className='text-[13px] tracking-tight'>General Comments</span>
                          <span className='text-[12px]' dir='rtl'>ملاحظات نتائج الأشعة</span>
                        </div>
                        <div className='space-y-[2px]'>
                          {allComments.map((item, idx) => (
                            <div key={idx} className='text-[12px] leading-tight flex items-start gap-1'>
                              <span className='font-bold whitespace-nowrap'>{item.i}. {item.testName}:</span>
                              <span className='text-gray-800'>{item.comment}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr>
              <td>
                <div className="page-footer-space"></div>
              </td>
            </tr>
          </tfoot>
        </table>

        <footer className='print-footer bg-white'>
          {useCbahiHeader ? (
            <div className='border-t border-gray-200 px-3 pb-0 pt-1'>
              <div className='flex items-center justify-between mb-1'>
                <div className='text-[11px] text-gray-700 flex items-baseline'>
                  <strong className='font-bold mr-2'>{t('Received By')}:</strong>
                  <span className='text-gray-600'>{performerInfo.receivedBy}</span>
                </div>
                <div className='text-[11px] text-gray-700 flex items-baseline'>
                  <strong className='font-bold mr-2'>{t('Approved By')}:</strong>
                  <span className='text-gray-600'>{performerInfo.approvedBy}</span>
                </div>
              </div>
              {/* {(performerInfo.signatureUrl || performerInfo.stampUrl) && (
                <div className='flex justify-end gap-6 mb-1'>
                  <div className='w-[110px] text-center'>
                    {performerInfo.signatureUrl && <img src={performerInfo.signatureUrl} alt={t('Approver signature')} className='h-10 w-full object-contain' />}
                    <div className='border-t border-gray-400 text-[9px] text-gray-600'>{t('SIGNATURE')}</div>
                  </div>
                  <div className='w-[110px] text-center'>
                    {performerInfo.stampUrl && <img src={performerInfo.stampUrl} alt={t('Approver stamp')} className='h-10 w-full object-contain' />}
                    <div className='border-t border-gray-400 text-[9px] text-gray-600'>{t('STAMP')}</div>
                  </div>
                </div>
              )} */}
              <ChaiReportFooter
                businessInfo={buisnessInfo}
                qrValue={`MRN:${patient?.file_no || orderData?.subject?.file_number || ''}|ORD:${orderNo || ''}`}
              />
            </div>
          ) : (
            <div className='border-t border-gray-200 px-3 pb-2 pt-2'>
              <div className='flex items-center justify-between mb-1'>
                <div className='text-[11px] text-gray-700 flex items-baseline'>
                  <strong className='font-bold mr-2'>{t('Received By')}:</strong>
                  <span className='text-gray-600'>{performerInfo.receivedBy}</span>
                </div>
                <div className='text-[11px] text-gray-700 flex items-baseline'>
                  <strong className='font-bold mr-2'>{t('Approved By')}:</strong>
                  <span className='text-gray-600'>{performerInfo.approvedBy}</span>
                </div>
              </div>

              <div className='flex items-center justify-between border-t border-gray-100 pt-1'>
                <div className='flex items-center'>
                  <span className='text-[10px] font-semibold mr-1'>{t('Address')}:</span>
                  <span className='text-[10px] text-gray-600'>{buisnessInfo?.address || 'N/A'}</span>
                </div>
                <div className='text-[10px] text-gray-400 italic'>
                  {t('Printed on')}: {formatDateTime(new Date())}
                </div>
              </div>
            </div>
          )}
        </footer>
      </div>

      <style>{`
                .comprehensive-header-repeat {
                    display: none;
                }
                .radiology-report-container {
                    width: 100%;
                }
                .report-page {
                    background: white;
                    padding: 0;
                    margin-bottom: 0px;
                }
                
                @media print {
                    @page {
                        margin: 0mm !important;
                        size: A4;
                    }
                    
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        transform: none !important;
                        width: 100% !important;
                        height: auto !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }

                    .page-header-space {
                      height: 10mm;
                    }

                    .page-footer-space {
                      height: ${isChaiFormat ? '25mm' : '20mm'};
                    }
                    
                    .radiology-report-container {
                        display: block;
                        width: 100%;
                    }

                    .report-page {
                        display: block;
                        width: 100%;
                        min-height: 270mm; 
                        margin: 0 !important;
                        padding: 0mm 7mm 0mm 7mm;
                        box-sizing: border-box;
                        position: relative;
                        background: white;
                        border: none;
                        overflow: visible;
                    }

                    .compact-report-page {
                        min-height: 0 !important;
                        height: auto !important;
                    }
                    
                    .print-footer {
                        position: fixed;
                        bottom: 0mm;
                        left: 0;
                        right: 0;
                        text-align: center;
                        z-index: 10;
                    }

                    .comprehensive-print-content {
                        display: block;
                        width: 100%;
                        padding-top: ${showReportBarcode ? '84mm' : '74mm'} !important;
                        box-sizing: border-box;
                        -webkit-box-decoration-break: clone;
                        box-decoration-break: clone;
                    }

                    .comprehensive-header-repeat {
                        display: table-header-group !important;
                    }

                    .comprehensive-header-repeat .print-report-header {
                        display: block !important;
                        position: fixed;
                        top: 0;
                        left: 7mm;
                        right: 7mm;
                        background: white;
                        z-index: 11;
                    }

                    .comprehensive-print-content .comprehensive-body-header {
                        display: none !important;
                    }
                    .report-section,
                    .print-avoid-break {
                        page-break-inside: avoid;
                        break-inside: avoid;
                        margin-bottom: 10px;
                    }

                    .compact-report-page .report-section {
                        page-break-inside: auto !important;
                        break-inside: auto !important;
                        margin-bottom: 4px;
                    }

                    .print-table {
                        width: 100%;
                    }

                    table {
                        page-break-inside: auto;
                    }

                    tr {
                        page-break-inside: avoid;
                        break-inside: avoid;
                    }

                    thead {
                        display: table-header-group;
                    }

                    tfoot {
                      display: table-footer-group;
                    }
                }
            `}</style>
    </div>
  );
};

export default RadiologyReportPrint;
